import { useDispatch, useSelector } from "react-redux";
import { patchActiveWarrant, patchActiveWarrantArray } from "@/slices/warrantsSlice";

/**
 * useActiveWarrant
 *
 * Shared hook used by every sub-form inside WarrantFullDetails.
 *
 * Usage:
 *   const { activeWarrant, bookingsFieldsEnabled, patch, patchArray } = useActiveWarrant();
 *
 *   // Patch flat fields directly onto the active warrant:
 *   patch({ warrantNumber: "12345", charge: "theft" });
 *
 *   // Patch a sub-array (warrantCharges, warrantStateInfo, etc.):
 *   patchArray("warrantCharges", updatedChargesArray);
 */
const useActiveWarrant = () => {
  const dispatch = useDispatch();

  const { activeWarrant, isSaving } = useSelector((s) => s?.warrants ?? {});
  const { bookingsFieldsEnabled }   = useSelector((s) => s?.bookings);

  /**
   * Patch any flat field(s) on the active warrant.
   * @param {object} fields — e.g. { warrantNumber: "X", charge: "Y" }
   */
  const patch = (fields) => {
    dispatch(patchActiveWarrant(fields));
  };

  /**
   * Replace a sub-array on the active warrant.
   * @param {string} key   — e.g. "warrantCharges"
   * @param {Array}  value — the new array value
   */
  const patchArray = (key, value) => {
    dispatch(patchActiveWarrantArray({ key, value }));
  };

  /**
   * Helper: patch a single item inside a sub-array by index.
   * @param {string} arrayKey — e.g. "warrantExtraditionInfo"
   * @param {number} index
   * @param {object} fields   — fields to merge into that item
   */
  const patchArrayItem = (arrayKey, index, fields) => {
    const current = activeWarrant?.[arrayKey] ?? [];

    let updated;
    if (current.length > index) {
      // ← item exists, merge into it
      updated = current.map((item, i) =>
        i === index ? { ...item, ...fields } : item
      );
    } else {
      // ← item doesn't exist yet, create it at the index
      updated = [...current];
      updated[index] = { ...fields };
    }

    dispatch(patchActiveWarrantArray({ key: arrayKey, value: updated }));
  };

  return {
    activeWarrant,
    bookingsFieldsEnabled,
    isSaving,
    patch,
    patchArray,
    patchArrayItem,
  };
};

export default useActiveWarrant;
